import {
  deleteDoc,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import type { AddedByUser, CompanyMember, UserRole } from './models';

const db = getFirestore();

const memberRef = (companyId: string, userId: string) =>
  doc(db, 'companies', companyId, 'members', userId);

/**
 * Get a single active member document from the members subcollection.
 * Returns null if the member does not exist or is not active.
 */
export const getActiveMember = async (
  companyId: string,
  userId: string
): Promise<CompanyMember | null> => {
  try {
    const memberDoc = await getDoc(memberRef(companyId, userId));

    if (!memberDoc.exists()) return null;

    const data = memberDoc.data();
    if (data.status !== 'active') return null;

    return {
      ...data,
      addedAt: data.addedAt?.toDate() || new Date(),
    } as CompanyMember;
  } catch (error) {
    console.error('[members] Error getting active member:', error);
    return null;
  }
};

/**
 * Subscribe to real-time updates for a member document.
 * Calls the callback with null when the member no longer exists or becomes inactive.
 */
export const subscribeToMember = (
  companyId: string,
  userId: string,
  callback: (member: CompanyMember | null) => void
): (() => void) => {
  return onSnapshot(memberRef(companyId, userId), (snapshot) => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }

    const data = snapshot.data();
    if (data.status !== 'active') {
      callback(null);
      return;
    }

    callback({
      ...data,
      addedAt: data.addedAt?.toDate() || new Date(),
    } as CompanyMember);
  });
};

export const addMember = async (
  companyId: string,
  memberData: {
    userId: string;
    email: string;
    name: string;
    role: UserRole;
  },
  addedBy: AddedByUser
): Promise<void> => {
  const newMember: CompanyMember = {
    ...memberData,
    addedAt: new Date(),
    addedBy,
    status: 'pending',
  };

  await setDoc(memberRef(companyId, memberData.userId), {
    ...newMember,
    addedAt: Timestamp.fromDate(newMember.addedAt),
  });
};

export const removeMember = async (
  companyId: string,
  userId: string
): Promise<void> => {
  await deleteDoc(memberRef(companyId, userId));
};

export const updateMemberRole = async (
  companyId: string,
  userId: string,
  newRole: UserRole
): Promise<void> => {
  await updateDoc(memberRef(companyId, userId), { role: newRole });
};
